import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@munawb/hooks';
import { supabase } from '@munawb/supabase';
import type { ProfileRow, IncidentRow } from '@munawb/types';
import { USER_ROLE_VALUES, ROLE_LABELS } from '@munawb/types';
import { formatDate } from '../lib/utils';

type TabId = 'overview' | 'users' | 'settings' | 'dictionaries' | 'reports' | 'archive';

/* ─── Types ──────────────────────────────────────────────────────────────── */

type PendingReg = {
  id:         string;
  name:       string;
  email:      string;
  phone:      string | null;
  title:      string | null;
  dept_id:    string | null;
  status:     string;
  created_at: string;
  departments: { name: string } | null;
};

type DeptRow = { id: string; name: string; active: boolean };

type CategoryRow = { id: string; main_cat: string; sub_cat: string | null; active: boolean };

type SettingRow = { key: string; label: string; value: string | null };

type UserWithDept = ProfileRow & { departments: { name: string } | null };

/* ─── Toast ──────────────────────────────────────────────────────────────── */

type ToastItem = { id: number; msg: string; type: 'success' | 'error' | 'info' };
let _toastId = 0;
function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const push = (msg: string, type: ToastItem['type'] = 'info') => {
    const id = ++_toastId;
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  };
  return { toasts, push };
}

/* ═══════════════════════════════════════════════════════════════════════════
   AdminPage
═══════════════════════════════════════════════════════════════════════════ */

export default function AdminPage() {
  const { profile }  = useAuthStore();
  const qc           = useQueryClient();
  const { toasts, push } = useToast();
  const [activeTab,   setActiveTab]   = useState<TabId>('overview');
  const [darkMode,    setDarkMode]    = useState(() => document.documentElement.classList.contains('dark'));
  const [showLogout,  setShowLogout]  = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  /* ─── Dark mode ── */
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('munawb-dark', darkMode ? '1' : '0');
  }, [darkMode]);

  /* ─── Queries ──────────────────────────────────────────────────────────── */

  const { data: pendingRegs = [], isLoading: pendingLoading } = useQuery<PendingReg[]>({
    queryKey: ['pending-registrations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pending_registrations')
        .select('*, departments(name)')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as PendingReg[];
    },
  });

  const { data: users = [], isLoading: usersLoading } = useQuery<UserWithDept[]>({
    queryKey: ['all-profiles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*, departments(name)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as UserWithDept[];
    },
    enabled: activeTab === 'users' || activeTab === 'overview',
  });

  const { data: incidents = [], isLoading: incidentsLoading } = useQuery<IncidentRow[]>({
    queryKey: ['admin-all-incidents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('incidents')
        .select('id, event_id, status, risk_level, sla_minutes, created_at, closed_at, deleted_at, dept_id, location, event_type, reporter_name, description')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as IncidentRow[];
    },
    enabled: activeTab === 'overview',
  });

  const { data: departments = [] } = useQuery<DeptRow[]>({
    queryKey: ['departments'],
    queryFn: async () => {
      const { data, error } = await supabase.from('departments').select('id, name, active').order('name');
      if (error) throw error;
      return data ?? [];
    },
    enabled: activeTab === 'dictionaries' || activeTab === 'overview',
  });

  const { data: categories = [] } = useQuery<CategoryRow[]>({
    queryKey: ['event-categories'],
    queryFn: async () => {
      const { data, error } = await supabase.from('event_categories').select('id, main_cat, sub_cat, active').order('main_cat');
      if (error) throw error;
      return (data ?? []) as CategoryRow[];
    },
    enabled: activeTab === 'dictionaries',
  });

  const { data: settings = [] } = useQuery<SettingRow[]>({
    queryKey: ['system-settings'],
    queryFn: async () => {
      const { data, error } = await supabase.from('system_settings').select('key, label, value').order('key');
      if (error) throw error;
      return (data ?? []) as SettingRow[];
    },
    enabled: activeTab === 'settings',
  });

  /* ─── Archive ── */
  const [archFrom, setArchFrom]     = useState('');
  const [archTo,   setArchTo]       = useState('');
  const [archSearch, setArchSearch] = useState('');
  const { data: archiveRaw = [], isFetching: archLoading, refetch: loadArchive } = useQuery<IncidentRow[]>({
    queryKey: ['archive-incidents'],
    queryFn: async () => {
      let q = supabase
        .from('incidents')
        .select('id, event_id, status, risk_level, created_at, closed_at, location, event_type, reporter_name, description')
        .eq('status', 'مغلق')
        .is('deleted_at', null)
        .order('closed_at', { ascending: false })
        .limit(500);
      if (archFrom) q = q.gte('closed_at', archFrom);
      if (archTo)   q = q.lte('closed_at', archTo + 'T23:59:59');
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as IncidentRow[];
    },
    enabled: false,
  });
  const archiveFiltered = archiveRaw.filter(i => {
    if (!archSearch) return true;
    const s = archSearch.toLowerCase();
    return (
      i.event_id.toLowerCase().includes(s) ||
      i.location.toLowerCase().includes(s) ||
      i.event_type.toLowerCase().includes(s) ||
      i.reporter_name.toLowerCase().includes(s) ||
      i.description.toLowerCase().includes(s)
    );
  });

  /* ─── Reports ── */
  const [reportFrom, setReportFrom]       = useState('');
  const [reportTo,   setReportTo]         = useState('');
  const [reportType, setReportType]       = useState('ALL');
  const [reportRows, setReportRows]       = useState<IncidentRow[]>([]);
  const [reportLoading, setReportLoading] = useState(false);

  const generateReport = async () => {
    setReportLoading(true);
    try {
      let q = supabase
        .from('incidents')
        .select('id, event_id, status, risk_level, created_at, closed_at, location, event_type, reporter_name, description')
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(50);
      if (reportFrom) q = q.gte('created_at', reportFrom);
      if (reportTo)   q = q.lte('created_at', reportTo + 'T23:59:59');
      if (reportType === 'CRITICAL') q = q.in('risk_level', ['حرج', 'عالي']);
      if (reportType === 'RCA')      q = q.in('status', ['طلب RCA', 'RCA Completed']);
      const { data, error } = await q;
      if (error) throw error;
      setReportRows((data ?? []) as IncidentRow[]);
    } catch (e: any) {
      push(e.message ?? 'فشل في توليد التقرير', 'error');
    } finally {
      setReportLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!reportRows.length) return;
    const headers = ['رقم الحدث', 'الحالة', 'مستوى الخطورة', 'الموقع', 'النوع', 'المُبلِّغ', 'تاريخ الإنشاء', 'تاريخ الإغلاق'];
    const rows = reportRows.map(r => [
      r.event_id, r.status, r.risk_level, r.location, r.event_type,
      r.reporter_name, formatDate(r.created_at), r.closed_at ? formatDate(r.closed_at) : '',
    ]);
    const csv = '\uFEFF' + [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `munawb-report-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ─── Reports: XLSX + Print ────────────────────────────────────────────── */
  const buildReportRows = (rows: IncidentRow[]) => rows.map(r => [
    r.event_id, r.status, r.risk_level, r.location, r.event_type,
    r.reporter_name, formatDate(r.created_at), r.closed_at ? formatDate(r.closed_at) : '',
  ]);

  const REPORT_HEADERS = ['رقم الحدث', 'الحالة', 'مستوى الخطورة', 'الموقع', 'النوع', 'المُبلِّغ', 'تاريخ الإنشاء', 'تاريخ الإغلاق'];

  const downloadXLSX = () => {
    if (!reportRows.length) return;
    const rows = buildReportRows(reportRows);
    const tsv  = '\uFEFF' + [REPORT_HEADERS, ...rows].map(r => r.join('\t')).join('\n');
    const blob  = new Blob([tsv], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url   = URL.createObjectURL(blob);
    const a     = document.createElement('a');
    a.href      = url;
    a.download  = `munawb-report-${new Date().toISOString().slice(0,10)}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printReport = () => {
    if (!reportRows.length) return;
    const rows = buildReportRows(reportRows);
    const w = window.open('', '_blank');
    if (!w) return;
    const tbody = rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('');
    w.document.write(`<!DOCTYPE html><html dir="rtl"><head><meta charset="UTF-8">
      <title>تقرير مُناوب</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800&display=swap');
        * { font-family: 'Tajawal', sans-serif; margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #fff; color: #0f172a; padding: 28px; font-size: 12px; }
        .header { border-bottom: 3px solid #dc2626; padding-bottom: 14px; margin-bottom: 20px; }
        .header h1 { font-size: 20px; font-weight: 800; }
        .header p  { font-size: 11px; color: #64748b; margin-top: 4px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #f1f5f9; font-weight: 800; padding: 7px 10px; font-size: 11px; text-align: right; border: 1px solid #e2e8f0; }
        td { padding: 6px 10px; border: 1px solid #e2e8f0; }
        tr:nth-child(even) td { background: #f8fafc; }
        .footer { margin-top: 24px; text-align: center; font-size: 11px; color: #94a3b8; }
        @page { size: A4 landscape; margin: 12mm; }
      </style></head><body>
      <div class="header">
        <h1>📋 تقرير الأحداث التشغيلية</h1>
        <p>المشرف: ${profile?.name ?? ''} — ${new Date().toLocaleString('ar-SA')}</p>
      </div>
      <table>
        <thead><tr>${REPORT_HEADERS.map(h => `<th>${h}</th>`).join('')}</tr></thead>
        <tbody>${tbody}</tbody>
      </table>
      <div class="footer">نظام مُناوب — ${new Date().toLocaleDateString('ar-SA')}</div>
    </body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  };

  /* ─── Archive export ─────────────────────────────────────────────────────── */
  const ARCH_HEADERS = ['رقم الحدث', 'مستوى الخطورة', 'الموقع', 'النوع', 'المُبلِّغ', 'تاريخ الإنشاء', 'تاريخ الإغلاق'];

  const buildArchRows = (rows: IncidentRow[]) => rows.map(r => [
    r.event_id, r.risk_level, r.location, r.event_type,
    r.reporter_name, formatDate(r.created_at), r.closed_at ? formatDate(r.closed_at) : '',
  ]);

  const downloadArchiveCSV = () => {
    if (!archiveFiltered.length) return;
    const csv = '\uFEFF' + [ARCH_HEADERS, ...buildArchRows(archiveFiltered)]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `munawb-archive-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadArchiveXLSX = () => {
    if (!archiveFiltered.length) return;
    const tsv  = '\uFEFF' + [ARCH_HEADERS, ...buildArchRows(archiveFiltered)].map(r => r.join('\t')).join('\n');
    const blob  = new Blob([tsv], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url   = URL.createObjectURL(blob);
    const a     = document.createElement('a');
    a.href      = url;
    a.download  = `munawb-archive-${new Date().toISOString().slice(0,10)}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ─── Mutations: Approve / Reject ──────────────────────────────────────── */
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const doApprove = async (regId: string) => {
    setActionLoading(true);
    try {
      const { data: tokenData } = await supabase
        .from('pending_registrations').select('token').eq('id', regId).single();
      if (!tokenData?.token) throw new Error('رمز التحقق غير موجود');
      const session = (await supabase.auth.getSession()).data.session;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth-approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token ?? ''}`,
          },
          body: JSON.stringify({ token: tokenData.token, action: 'approve' }),
        }
      );
      if (!res.ok) { const j = await res.json(); throw new Error(j.error ?? 'فشل في قبول الطلب'); }
      push('تم قبول الطلب وإنشاء الحساب بنجاح', 'success');
      qc.invalidateQueries({ queryKey: ['pending-registrations'] });
    } catch (e: any) {
      push(e.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const doReject = async (regId: string) => {
    setActionLoading(true);
    try {
      const { data: tokenData } = await supabase
        .from('pending_registrations').select('token').eq('id', regId).single();
      if (!tokenData?.token) throw new Error('رمز التحقق غير موجود');
      const session = (await supabase.auth.getSession()).data.session;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth-approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token ?? ''}`,
          },
          body: JSON.stringify({ token: tokenData.token, action: 'reject', rejection_reason: rejectReason }),
        }
      );
      if (!res.ok) { const j = await res.json(); throw new Error(j.error ?? 'فشل في رفض الطلب'); }
      push('تم رفض الطلب', 'info');
      setRejectTarget(null);
      setRejectReason('');
      qc.invalidateQueries({ queryKey: ['pending-registrations'] });
    } catch (e: any) {
      push(e.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  /* ─── Mutations: User Role / Status ──────────────────────────────────── */
  const { mutate: updateUserRole } = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const { error } = await supabase.from('profiles').update({ role }).eq('id', userId);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['all-profiles'] }); push('تم تحديث الدور', 'success'); },
    onError:   (e: any) => push(e.message, 'error'),
  });

  const { mutate: updateUserStatus } = useMutation({
    mutationFn: async ({ userId, status }: { userId: string; status: string }) => {
      const { error } = await supabase.from('profiles').update({ status }).eq('id', userId);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['all-profiles'] }); push('تم تحديث الحالة', 'success'); },
    onError:   (e: any) => push(e.message, 'error'),
  });

  /* ─── Mutations: Departments ─────────────────────────────────────────── */
  const [newDeptName, setNewDeptName] = useState('');
  const { mutate: addDept } = useMutation({
    mutationFn: async (name: string) => {
      const { error } = await supabase.from('departments').insert({ name });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['departments'] });
      setNewDeptName('');
      push('تمت إضافة القسم', 'success');
    },
    onError: (e: any) => push(e.message, 'error'),
  });

  const { mutate: toggleDept } = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from('departments').update({ active }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['departments'] }),
    onError:   (e: any) => push(e.message, 'error'),
  });

  /* ─── Mutations: Categories ──────────────────────────────────────────── */
  const [catMainSel, setCatMainSel] = useState('');
  const [catNewMain, setCatNewMain] = useState('');
  const [catNewSub,  setCatNewSub]  = useState('');

  const mainCats = [...new Set(categories.map(c => c.main_cat))];

  const { mutate: addCategory } = useMutation({
    mutationFn: async () => {
      const main = catMainSel === '__new__' ? catNewMain.trim() : catMainSel;
      if (!main || !catNewSub.trim()) throw new Error('اسم التصنيف الرئيسي والفرعي مطلوبان');
      const { error } = await supabase.from('event_categories').insert({ main_cat: main, sub_cat: catNewSub.trim() });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event-categories'] });
      setCatNewSub('');
      setCatNewMain('');
      push('تمت إضافة التصنيف', 'success');
    },
    onError: (e: any) => push(e.message, 'error'),
  });

  const { mutate: toggleCategory } = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from('event_categories').update({ active }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['event-categories'] }),
    onError:   (e: any) => push(e.message, 'error'),
  });

  /* ─── Overview KPIs ─────────────────────────────────────────────────── */
  const totalIncidents   = incidents.length;
  const openIncidents    = incidents.filter(i => i.status !== 'مغلق' && i.status !== 'RCA Completed').length;
  const slaBreached      = incidents.filter(i => {
    if (i.status === 'مغلق' || i.status === 'RCA Completed') return false;
    const elapsed = (Date.now() - new Date(i.created_at).getTime()) / 60000;
    return elapsed > i.sla_minutes;
  }).length;
  const activeUsers = users.filter(u => u.status === 'active').length;

  // Chart data for status distribution
  const statusGroups: Record<string, number> = {};
  incidents.forEach(i => { statusGroups[i.status] = (statusGroups[i.status] ?? 0) + 1; });
  const statusEntries = Object.entries(statusGroups).sort((a, b) => b[1] - a[1]);

  // Risk level distribution
  const riskGroups: Record<string, number> = {};
  incidents.forEach(i => { riskGroups[i.risk_level] = (riskGroups[i.risk_level] ?? 0) + 1; });
  const riskEntries = Object.entries(riskGroups).sort((a, b) => b[1] - a[1]);

  // Role distribution
  const roleGroups: Record<string, number> = {};
  users.forEach(u => { roleGroups[u.role] = (roleGroups[u.role] ?? 0) + 1; });
  const roleEntries = Object.entries(roleGroups).sort((a, b) => b[1] - a[1]);

  const statusColors: Record<string, string> = {
    'مفتوح': '#3b82f6', 'مصعد': '#ef4444', 'موجه': '#8b5cf6',
    'طلب RCA': '#f59e0b', 'مغلق': '#10b981', 'RCA Completed': '#06b6d4',
  };
  const riskColors: Record<string, string> = {
    'منخفض': '#10b981', 'متوسط': '#f59e0b', 'عالي': '#f97316', 'مرتفع': '#ef4444', 'حرج': '#7c3aed',
  };

  /* ─── Settings local state ─────────────────────────────────────────── */
  const [settingsLocal, setSettingsLocal] = useState<Record<string, string>>({});
  const [settingsSaving, setSettingsSaving] = useState(false);

  useEffect(() => {
    const map: Record<string, string> = {};
    settings.forEach(s => { map[s.key] = s.value ?? ''; });
    setSettingsLocal(map);
  }, [settings]);

  const saveAllSettings = async () => {
    setSettingsSaving(true);
    try {
      await Promise.all(
        Object.entries(settingsLocal).map(([key, value]) =>
          supabase.from('system_settings').update({ value, updated_by: profile?.id }).eq('key', key)
        )
      );
      qc.invalidateQueries({ queryKey: ['system-settings'] });
      push('تم حفظ الإعدادات بنجاح', 'success');
    } catch (e: any) {
      push(e.message, 'error');
    } finally {
      setSettingsSaving(false);
    }
  };

  /* ─── Search state for users tab ──────────────────────────────────── */
  const [userSearch, setUserSearch] = useState('');
  const [showPending, setShowPending] = useState(true);

  const filteredUsers = users.filter(u => {
    if (!userSearch) return true;
    const s = userSearch.toLowerCase();
    return u.name.toLowerCase().includes(s) || (u.departments?.name ?? '').toLowerCase().includes(s);
  });

  const handleSignOut = async () => {
    setShowLogout(false);
    await supabase.auth.signOut();
  };

  /* ─── Tabs ───────────────────────────────────────────────────────── */
  const TABS: { id: TabId; label: string; badge?: number }[] = [
    { id: 'overview',      label: 'نظرة عامة' },
    { id: 'users',         label: 'إدارة المستخدمين', badge: pendingRegs.length },
    { id: 'settings',      label: 'إعدادات النظام' },
    { id: 'dictionaries',  label: 'الأقسام والتصنيفات' },
    { id: 'reports',       label: 'التقارير' },
    { id: 'archive',       label: 'الأرشيف' },
  ];

  /* ════════════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950" dir="rtl">

      {/* ── Toasts ── */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2 items-center pointer-events-none">
        {toasts.map(t => (
          <div key={t.id} className={`px-5 py-3 rounded-2xl text-white text-sm font-black shadow-xl pointer-events-auto ${
            t.type === 'success' ? 'bg-emerald-600' : t.type === 'error' ? 'bg-red-600' : 'bg-blue-600'
          }`}>
            {t.msg}
          </div>
        ))}
      </div>

      {/* ── Header ── */}
      <header className="glass-panel border-b border-slate-200/80 dark:border-slate-700/50 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <img src="/munawb-logo.png" alt="" className="w-10 h-10 rounded-xl" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            <div>
              <h1 className="font-black text-lg text-slate-800 dark:text-white flex items-center gap-2">
                لوحة القيادة
                <span className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-[10px] px-2 py-0.5 rounded-full font-black border border-red-200 dark:border-red-800 uppercase">Admin</span>
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">إدارة النظام، المستخدمين، والمؤشرات</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-700 dark:text-slate-300 hidden sm:block">{profile?.name}</span>
            <button
              onClick={() => setShowProfile(true)}
              title="الملف الشخصي"
              className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:scale-110 transition border border-slate-200 dark:border-slate-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A9 9 0 1118.88 6.196M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <button
              onClick={() => setDarkMode(d => !d)}
              className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-yellow-400 hover:scale-110 transition border border-slate-200 dark:border-slate-700 text-sm"
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
            <button onClick={() => setShowLogout(true)} className="btn-secondary text-xs px-3 py-1.5">خروج</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 flex overflow-x-auto gap-1 pb-1">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative px-4 py-2.5 font-black text-sm whitespace-nowrap transition-all border-b-2 ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
            >
              {tab.label}
              {tab.badge != null && tab.badge > 0 && (
                <span className="absolute -top-1 -left-1 w-5 h-5 bg-red-500 rounded-full text-white text-[10px] font-black flex items-center justify-center">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {/* ══ OVERVIEW ══════════════════════════════════════════════════════ */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard
                label="إجمالي أحداث المنشأة"
                value={incidentsLoading ? '--' : totalIncidents}
                color="blue"
                icon="🏥"
              />
              <KpiCard
                label="الأحداث المفتوحة قيد العمل"
                value={incidentsLoading ? '--' : openIncidents}
                color="orange"
                icon="📂"
              />
              <KpiCard
                label="تجاوزت SLA الشامل"
                value={incidentsLoading ? '--' : slaBreached}
                color="red"
                icon="⚠️"
              />
              <KpiCard
                label="إجمالي المستخدمين النشطين"
                value={usersLoading ? '--' : activeUsers}
                color="emerald"
                icon="👥"
              />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

              {/* Status Distribution */}
              <div className="glass-panel rounded-2xl p-5">
                <p className="text-xs font-black text-slate-500 dark:text-slate-400 mb-4">توزيع حالات الأحداث</p>
                {incidentsLoading ? (
                  <ChartSkeleton />
                ) : statusEntries.length === 0 ? (
                  <p className="text-center text-slate-400 text-xs py-8">لا توجد بيانات</p>
                ) : (
                  <div className="space-y-2.5">
                    {statusEntries.map(([status, count]) => {
                      const pct = totalIncidents > 0 ? Math.round((count / totalIncidents) * 100) : 0;
                      return (
                        <div key={status}>
                          <div className="flex justify-between text-xs font-bold mb-1">
                            <span className="text-slate-700 dark:text-slate-300">{status}</span>
                            <span className="text-slate-500">{count} ({pct}%)</span>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{ width: `${pct}%`, backgroundColor: statusColors[status] ?? '#94a3b8' }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Risk Level Distribution */}
              <div className="glass-panel rounded-2xl p-5">
                <p className="text-xs font-black text-slate-500 dark:text-slate-400 mb-4">توزيع مستويات الخطورة</p>
                {incidentsLoading ? (
                  <ChartSkeleton />
                ) : riskEntries.length === 0 ? (
                  <p className="text-center text-slate-400 text-xs py-8">لا توجد بيانات</p>
                ) : (
                  <div className="space-y-2.5">
                    {riskEntries.map(([level, count]) => {
                      const pct = totalIncidents > 0 ? Math.round((count / totalIncidents) * 100) : 0;
                      return (
                        <div key={level}>
                          <div className="flex justify-between text-xs font-bold mb-1">
                            <span className="text-slate-700 dark:text-slate-300">{level}</span>
                            <span className="text-slate-500">{count} ({pct}%)</span>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{ width: `${pct}%`, backgroundColor: riskColors[level] ?? '#94a3b8' }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Role Distribution */}
              <div className="glass-panel rounded-2xl p-5">
                <p className="text-xs font-black text-slate-500 dark:text-slate-400 mb-4">توزيع أدوار المستخدمين</p>
                {usersLoading ? (
                  <ChartSkeleton />
                ) : roleEntries.length === 0 ? (
                  <p className="text-center text-slate-400 text-xs py-8">لا توجد بيانات</p>
                ) : (
                  <div className="space-y-2.5">
                    {roleEntries.map(([role, count]) => {
                      const maxRole = Math.max(...roleEntries.map(r => r[1]));
                      const pct = maxRole > 0 ? Math.round((count / maxRole) * 100) : 0;
                      return (
                        <div key={role}>
                          <div className="flex justify-between text-xs font-bold mb-1">
                            <span className="text-slate-700 dark:text-slate-300">{ROLE_LABELS[role as keyof typeof ROLE_LABELS] ?? role}</span>
                            <span className="text-slate-500">{count}</span>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="glass-panel rounded-2xl p-5">
              <p className="text-sm font-black text-slate-700 dark:text-white mb-4">إحصائيات سريعة</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
                  <p className="text-2xl font-black text-blue-600">{departments.filter(d => d.active).length}</p>
                  <p className="text-xs text-slate-500 font-bold mt-1">أقسام نشطة</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
                  <p className="text-2xl font-black text-amber-600">{pendingRegs.length}</p>
                  <p className="text-xs text-slate-500 font-bold mt-1">طلبات معلقة</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
                  <p className="text-2xl font-black text-purple-600">{incidents.filter(i => i.status === 'مصعد').length}</p>
                  <p className="text-xs text-slate-500 font-bold mt-1">أحداث مصعدة</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3">
                  <p className="text-2xl font-black text-emerald-600">{incidents.filter(i => i.status === 'مغلق').length}</p>
                  <p className="text-xs text-slate-500 font-bold mt-1">أحداث مغلقة</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ USERS ═════════════════════════════════════════════════════════ */}
        {activeTab === 'users' && (
          <div className="space-y-4">

            {/* Toggle: Pending / All */}
            <div className="flex gap-2">
              <button
                onClick={() => setShowPending(true)}
                className={`relative px-5 py-2.5 rounded-xl font-black text-sm transition-all ${
                  showPending
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50'
                }`}
              >
                طلبات التسجيل
                {pendingRegs.length > 0 && (
                  <span className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-red-500 rounded-full text-white text-[10px] font-black flex items-center justify-center">
                    {pendingRegs.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowPending(false)}
                className={`px-5 py-2.5 rounded-xl font-black text-sm transition-all ${
                  !showPending
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50'
                }`}
              >
                جميع المستخدمين
              </button>
            </div>

            {/* Pending Registrations */}
            {showPending && (
              <div className="space-y-3">
                {pendingLoading ? (
                  <div className="glass-panel rounded-2xl p-10 text-center">
                    <span className="spinner border-blue-200 border-t-blue-600" />
                  </div>
                ) : pendingRegs.length === 0 ? (
                  <div className="glass-panel rounded-2xl p-10 text-center">
                    <p className="text-5xl mb-3">✅</p>
                    <p className="text-slate-400 font-bold text-sm">لا توجد طلبات تسجيل معلقة</p>
                  </div>
                ) : pendingRegs.map(reg => (
                  <div key={reg.id} className="glass-panel rounded-2xl p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <p className="font-black text-slate-800 dark:text-white">{reg.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5" dir="ltr">{reg.email}</p>
                        {reg.phone && <p className="text-xs text-slate-400 mt-0.5" dir="ltr">{reg.phone}</p>}
                        {reg.title && <p className="text-xs text-slate-400 mt-0.5">{reg.title}</p>}
                        {reg.departments && <p className="text-xs text-blue-600 dark:text-blue-400 font-bold mt-0.5">{reg.departments.name}</p>}
                        <p className="text-[11px] text-slate-400 mt-1">{formatDate(reg.created_at)}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setRejectTarget(reg.id)}
                          className="btn-danger text-xs px-3 py-1.5"
                          disabled={actionLoading}
                        >
                          رفض
                        </button>
                        <button
                          onClick={() => doApprove(reg.id)}
                          className="text-xs px-4 py-1.5 rounded-xl font-black text-white bg-emerald-600 hover:bg-emerald-700 transition"
                          disabled={actionLoading}
                        >
                          {actionLoading ? <span className="spinner border-white/30 border-t-white" /> : 'قبول'}
                        </button>
                      </div>
                    </div>

                    {rejectTarget === reg.id && (
                      <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 space-y-2">
                        <input
                          type="text"
                          className="input-field text-sm"
                          placeholder="سبب الرفض (اختياري)"
                          value={rejectReason}
                          onChange={e => setRejectReason(e.target.value)}
                        />
                        <div className="flex gap-2">
                          <button onClick={() => { setRejectTarget(null); setRejectReason(''); }} className="btn-secondary text-xs flex-1">إلغاء</button>
                          <button
                            onClick={() => doReject(reg.id)}
                            className="btn-danger text-xs flex-1"
                            disabled={actionLoading}
                          >
                            {actionLoading ? <span className="spinner border-white/30 border-t-white" /> : 'تأكيد الرفض'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* All Users Table */}
            {!showPending && (
              <div className="glass-panel rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex gap-3">
                  <input
                    type="text"
                    className="input-field flex-1 text-sm"
                    placeholder="بحث باسم المستخدم أو القسم..."
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                  />
                </div>
                {usersLoading ? (
                  <div className="p-10 text-center"><span className="spinner border-blue-200 border-t-blue-600" /></div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 dark:bg-slate-800/50">
                        <tr>
                          {['الاسم', 'البريد / القسم', 'الدور', 'الحالة', 'تاريخ الإنشاء'].map(h => (
                            <th key={h} className="px-4 py-3 text-right font-black text-xs text-slate-500 dark:text-slate-400">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map(u => (
                          <tr key={u.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                            <td className="px-4 py-3 font-bold text-slate-800 dark:text-white">{u.name}</td>
                            <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">
                              <span className="text-blue-600 dark:text-blue-400">{u.departments?.name ?? '—'}</span>
                            </td>
                            <td className="px-4 py-3">
                              <select
                                className="text-xs font-bold bg-transparent border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 focus:ring-1 focus:ring-blue-500"
                                value={u.role}
                                onChange={e => updateUserRole({ userId: u.id, role: e.target.value })}
                                disabled={u.id === profile?.id}
                              >
                                {USER_ROLE_VALUES.map(r => (
                                  <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-3">
                              <select
                                className="text-xs font-bold bg-transparent border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 focus:ring-1 focus:ring-blue-500"
                                value={u.status}
                                onChange={e => updateUserStatus({ userId: u.id, status: e.target.value })}
                                disabled={u.id === profile?.id}
                              >
                                {['active', 'pending', 'suspended', 'rejected'].map(s => (
                                  <option key={s} value={s}>{
                                    s === 'active' ? 'نشط' : s === 'pending' ? 'معلق' : s === 'suspended' ? 'موقوف' : 'مرفوض'
                                  }</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-400">{formatDate(u.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredUsers.length === 0 && (
                      <p className="text-center text-slate-400 text-sm py-8">لا توجد نتائج</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ══ SETTINGS ══════════════════════════════════════════════════════ */}
        {activeTab === 'settings' && (
          <div className="space-y-6">

            {/* Visual Identity */}
            <div className="glass-panel rounded-2xl p-6">
              <h3 className="font-black text-base text-slate-800 dark:text-white mb-4 pb-2 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
                🎨 الهوية البصرية والنصوص
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {settings.filter(s => ['logo_url', 'footer_text', 'frontend_url', 'system_name'].includes(s.key)).map(s => (
                  <div key={s.key} className={s.key === 'frontend_url' ? 'md:col-span-2' : ''}>
                    <label className="block text-xs font-bold mb-1 text-slate-700 dark:text-slate-300">{s.label ?? s.key}</label>
                    <input
                      type="text"
                      className="input-field text-sm"
                      dir={s.key.endsWith('_url') ? 'ltr' : 'rtl'}
                      value={settingsLocal[s.key] ?? ''}
                      onChange={e => setSettingsLocal(prev => ({ ...prev, [s.key]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* SLA Settings */}
            <div className="glass-panel rounded-2xl p-6">
              <h3 className="font-black text-base text-slate-800 dark:text-white mb-4 pb-2 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
                ⏱️ معايير الزمن (SLA) بالدقائق
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {[
                  { key_prefix: 'sla_low',    label: 'منخفض (Low)',       colorClass: 'border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800',   textClass: 'text-blue-800 dark:text-blue-300' },
                  { key_prefix: 'sla_medium',  label: 'متوسط (Medium)',    colorClass: 'border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800', textClass: 'text-yellow-800 dark:text-yellow-300' },
                  { key_prefix: 'sla_high',    label: 'عالي (High)',       colorClass: 'border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800', textClass: 'text-orange-800 dark:text-orange-300' },
                  { key_prefix: 'sla_critical',label: 'حرج (Critical)',    colorClass: 'border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800',     textClass: 'text-red-800 dark:text-red-300' },
                ].map(tier => (
                  <div key={tier.key_prefix} className={`p-4 rounded-xl border ${tier.colorClass}`}>
                    <h4 className={`font-black text-sm mb-3 pb-1 border-b ${tier.colorClass} ${tier.textClass}`}>{tier.label}</h4>
                    <div className="space-y-2">
                      {['_response_min', '_close_min', '_notify_repeat_min'].map(suffix => {
                        const key = `${tier.key_prefix}${suffix}`;
                        const setting = settings.find(s => s.key === key);
                        if (!setting) return null;
                        return (
                          <div key={key}>
                            <label className={`text-[10px] font-bold ${tier.textClass}`}>{setting.label ?? key}</label>
                            <input
                              type="number"
                              min="1"
                              className="w-full px-2 py-1 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-center font-bold text-sm mt-0.5 focus:ring-1 focus:ring-blue-500"
                              value={settingsLocal[key] ?? ''}
                              onChange={e => setSettingsLocal(prev => ({ ...prev, [key]: e.target.value }))}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Email Templates */}
            <div className="glass-panel rounded-2xl p-6">
              <h3 className="font-black text-base text-slate-800 dark:text-white mb-4 pb-2 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
                ✉️ قوالب البريد الإلكتروني
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {settings.filter(s => s.key.startsWith('tpl_')).map(s => (
                  <div key={s.key}>
                    <label className="block text-xs font-bold mb-1 text-slate-700 dark:text-slate-300">{s.label ?? s.key}</label>
                    <textarea
                      rows={4}
                      className="input-field text-xs leading-relaxed resize-y font-mono"
                      value={settingsLocal[s.key] ?? ''}
                      onChange={e => setSettingsLocal(prev => ({ ...prev, [s.key]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Other Settings */}
            {settings.filter(s =>
              !['logo_url','footer_text','frontend_url','system_name'].includes(s.key) &&
              !s.key.startsWith('sla_') && !s.key.startsWith('tpl_')
            ).length > 0 && (
              <div className="glass-panel rounded-2xl p-6">
                <h3 className="font-black text-base text-slate-800 dark:text-white mb-4 pb-2 border-b border-slate-200 dark:border-slate-700">
                  إعدادات أخرى
                </h3>
                <div className="space-y-3">
                  {settings
                    .filter(s =>
                      !['logo_url','footer_text','frontend_url','system_name'].includes(s.key) &&
                      !s.key.startsWith('sla_') && !s.key.startsWith('tpl_')
                    )
                    .map(s => (
                      <div key={s.key} className="flex items-center gap-4">
                        <div className="flex-1">
                          <label className="block text-xs font-black text-slate-500 dark:text-slate-400 mb-1">{s.label ?? s.key}</label>
                          <input
                            type="text"
                            className="input-field text-sm"
                            value={settingsLocal[s.key] ?? ''}
                            onChange={e => setSettingsLocal(prev => ({ ...prev, [s.key]: e.target.value }))}
                            dir="ltr"
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={saveAllSettings}
                disabled={settingsSaving}
                className="btn-primary px-8 py-3 flex items-center gap-2"
              >
                {settingsSaving ? <span className="spinner border-white/30 border-t-white" /> : null}
                حفظ جميع الإعدادات
              </button>
            </div>
          </div>
        )}

        {/* ══ DICTIONARIES ══════════════════════════════════════════════════ */}
        {activeTab === 'dictionaries' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Departments */}
            <div className="glass-panel rounded-2xl p-5 flex flex-col">
              <h3 className="font-black text-base text-slate-800 dark:text-white mb-4 pb-2 border-b border-slate-200 dark:border-slate-700">
                📁 إدارة الأقسام
              </h3>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  className="input-field flex-1 text-sm"
                  placeholder="اسم القسم الجديد..."
                  value={newDeptName}
                  onChange={e => setNewDeptName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && newDeptName.trim() && addDept(newDeptName.trim())}
                />
                <button
                  onClick={() => newDeptName.trim() && addDept(newDeptName.trim())}
                  className="btn-primary text-sm px-4"
                >
                  إضافة
                </button>
              </div>
              <div className="overflow-y-auto max-h-96 space-y-2 pl-1">
                {departments.length === 0 ? (
                  <p className="text-center text-slate-400 text-sm py-6">لا توجد أقسام</p>
                ) : departments.map(d => (
                  <div key={d.id} className="flex items-center justify-between px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50">
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-300">{d.name}</span>
                    <button
                      onClick={() => toggleDept({ id: d.id, active: !d.active })}
                      className={`text-xs font-black px-3 py-1 rounded-full transition-colors ${
                        d.active
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200'
                          : 'bg-slate-200 dark:bg-slate-700 text-slate-500 hover:bg-slate-300'
                      }`}
                    >
                      {d.active ? 'نشط' : 'معطّل'}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Event Categories */}
            <div className="glass-panel rounded-2xl p-5 flex flex-col">
              <h3 className="font-black text-base text-slate-800 dark:text-white mb-4 pb-2 border-b border-slate-200 dark:border-slate-700">
                🏷️ تصنيفات الأحداث الذكية
              </h3>
              <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-xl border border-indigo-100 dark:border-indigo-800 mb-4 space-y-2">
                <div>
                  <label className="block text-xs font-bold mb-1 text-indigo-800 dark:text-indigo-300">التصنيف الرئيسي</label>
                  <select
                    className="w-full input-field text-sm"
                    value={catMainSel}
                    onChange={e => { setCatMainSel(e.target.value); setCatNewMain(''); }}
                  >
                    <option value="">-- اختر أو أضف جديداً --</option>
                    {mainCats.map(c => <option key={c} value={c}>{c}</option>)}
                    <option value="__new__">+ إضافة تصنيف رئيسي جديد</option>
                  </select>
                </div>
                {catMainSel === '__new__' && (
                  <div>
                    <input
                      type="text"
                      className="input-field text-sm"
                      placeholder="اكتب اسم التصنيف الرئيسي الجديد..."
                      value={catNewMain}
                      onChange={e => setCatNewMain(e.target.value)}
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-bold mb-1 text-indigo-800 dark:text-indigo-300">النوع الفرعي</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input-field flex-1 text-sm"
                      placeholder="اكتب النوع الفرعي..."
                      value={catNewSub}
                      onChange={e => setCatNewSub(e.target.value)}
                    />
                    <button onClick={() => addCategory()} className="btn-primary text-sm px-4">حفظ</button>
                  </div>
                </div>
              </div>
              <div className="overflow-y-auto max-h-72 space-y-1.5 pl-1">
                {categories.length === 0 ? (
                  <p className="text-center text-slate-400 text-sm py-6">لا توجد تصنيفات</p>
                ) : categories.map(c => (
                  <div key={c.id} className="flex items-center justify-between px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50">
                    <div>
                      <span className="text-xs font-black text-indigo-600 dark:text-indigo-400">{c.main_cat}</span>
                      {c.sub_cat && <span className="text-xs text-slate-500 dark:text-slate-400 mr-2">/ {c.sub_cat}</span>}
                    </div>
                    <button
                      onClick={() => toggleCategory({ id: c.id, active: !c.active })}
                      className={`text-[10px] font-black px-2.5 py-0.5 rounded-full transition-colors ${
                        c.active
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                          : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
                      }`}
                    >
                      {c.active ? 'نشط' : 'معطّل'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ══ REPORTS ════════════════════════════════════════════════════════ */}
        {activeTab === 'reports' && (
          <div className="glass-panel rounded-2xl p-6 border-t-4 border-emerald-500">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-2xl flex items-center justify-center text-2xl">📊</div>
              <div>
                <h2 className="text-xl font-black text-slate-800 dark:text-white">مركز استخراج التقارير</h2>
                <p className="text-sm font-bold text-slate-500 dark:text-slate-400">حدد المعايير لاستخراج البيانات</p>
              </div>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
              <div>
                <label className="block text-xs font-black mb-1 text-slate-500 uppercase">من تاريخ</label>
                <input type="date" className="input-field text-sm" value={reportFrom} onChange={e => setReportFrom(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-black mb-1 text-slate-500 uppercase">إلى تاريخ</label>
                <input type="date" className="input-field text-sm" value={reportTo} onChange={e => setReportTo(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-black mb-1 text-slate-500 uppercase">نوع البيانات</label>
                <select className="input-field text-sm" value={reportType} onChange={e => setReportType(e.target.value)}>
                  <option value="ALL">جميع الأحداث التشغيلية</option>
                  <option value="CRITICAL">الأحداث الحرجة والعالية فقط</option>
                  <option value="RCA">سجل تحليلات RCA</option>
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mb-6">
              <button
                onClick={generateReport}
                disabled={reportLoading}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-black py-3 rounded-xl shadow-lg transition-all flex justify-center items-center gap-2"
              >
                {reportLoading ? <span className="spinner border-white/30 border-t-white" /> : '🔄'}
                توليد تقرير ومعاينة البيانات
              </button>
              {reportRows.length > 0 && (
                <>
                  <button
                    onClick={downloadCSV}
                    className="bg-slate-700 hover:bg-slate-800 text-white font-black py-3 px-5 rounded-xl shadow-lg transition-all flex items-center gap-2"
                  >
                    📄 CSV
                  </button>
                  <button
                    onClick={downloadXLSX}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white font-black py-3 px-5 rounded-xl shadow-lg transition-all flex items-center gap-2"
                  >
                    📊 XLSX
                  </button>
                  <button
                    onClick={printReport}
                    className="bg-blue-700 hover:bg-blue-800 text-white font-black py-3 px-5 rounded-xl shadow-lg transition-all flex items-center gap-2"
                  >
                    🖨️ طباعة
                  </button>
                </>
              )}
            </div>

            {/* Preview Table */}
            {reportRows.length > 0 && (
              <div>
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full animate-ping inline-block" />
                  معاينة النتائج (أحدث {reportRows.length} سجلاً)
                </p>
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-black">
                      <tr>
                        {['رقم الحدث', 'الحالة', 'الخطورة', 'الموقع', 'النوع', 'المُبلِّغ', 'تاريخ الإنشاء'].map(h => (
                          <th key={h} className="px-3 py-2 text-right">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {reportRows.map(r => (
                        <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                          <td className="px-3 py-2 font-black text-blue-600 dark:text-blue-400">{r.event_id}</td>
                          <td className="px-3 py-2 font-bold text-slate-600 dark:text-slate-300">{r.status}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full font-black text-[10px] ${
                              r.risk_level === 'حرج' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                              r.risk_level === 'عالي' || r.risk_level === 'مرتفع' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                              r.risk_level === 'متوسط' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                              'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            }`}>
                              {r.risk_level}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{r.location}</td>
                          <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{r.event_type}</td>
                          <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{r.reporter_name}</td>
                          <td className="px-3 py-2 text-slate-400">{formatDate(r.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {reportRows.length === 0 && !reportLoading && (
              <div className="text-center py-10 text-slate-400">
                <p className="text-4xl mb-2">📋</p>
                <p className="font-bold text-sm">اضغط "توليد تقرير" لعرض البيانات</p>
              </div>
            )}
          </div>
        )}

        {/* ══ ARCHIVE ════════════════════════════════════════════════════════ */}
        {activeTab === 'archive' && (
          <div className="glass-panel rounded-2xl p-6 border-t-4 border-amber-500">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-amber-100 dark:bg-amber-900/30 rounded-2xl flex items-center justify-center text-2xl">🗄️</div>
              <div>
                <h2 className="text-xl font-black text-slate-800 dark:text-white">أرشيف الأحداث المغلقة</h2>
                <p className="text-sm font-bold text-slate-500 dark:text-slate-400">الأحداث المغلقة — مؤرشفة للمراجعة</p>
              </div>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
              <div>
                <label className="block text-xs font-black mb-1 text-slate-500 uppercase">من تاريخ</label>
                <input type="date" className="input-field text-sm" value={archFrom} onChange={e => setArchFrom(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-black mb-1 text-slate-500 uppercase">إلى تاريخ</label>
                <input type="date" className="input-field text-sm" value={archTo} onChange={e => setArchTo(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-black mb-1 text-slate-500 uppercase">بحث</label>
                <input
                  type="text"
                  className="input-field text-sm"
                  placeholder="قسم، نوع، وصف، رقم الحدث..."
                  value={archSearch}
                  onChange={e => setArchSearch(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => loadArchive()}
                  disabled={archLoading}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white font-black py-2.5 rounded-xl shadow transition-all flex justify-center items-center gap-2"
                >
                  {archLoading ? <span className="spinner border-white/30 border-t-white" /> : '🔍'}
                  بحث في الأرشيف
                </button>
              </div>
            </div>

            {/* Archive Export Buttons */}
            {archiveFiltered.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-4">
                <button
                  onClick={downloadArchiveCSV}
                  className="bg-slate-700 hover:bg-slate-800 text-white font-black py-2.5 px-5 rounded-xl shadow transition-all flex items-center gap-2 text-sm"
                >
                  📄 تصدير CSV ({archiveFiltered.length})
                </button>
                <button
                  onClick={downloadArchiveXLSX}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white font-black py-2.5 px-5 rounded-xl shadow transition-all flex items-center gap-2 text-sm"
                >
                  📊 تصدير XLSX
                </button>
              </div>
            )}

            {/* Archive Table */}
            {archiveFiltered.length > 0 ? (
              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-black">
                    <tr>
                      {['رقم الحدث', 'الخطورة', 'الموقع', 'النوع', 'المُبلِّغ', 'تاريخ الإنشاء', 'تاريخ الإغلاق'].map(h => (
                        <th key={h} className="px-4 py-3 text-right text-xs">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {archiveFiltered.map(r => (
                      <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                        <td className="px-4 py-3 font-black text-blue-600 dark:text-blue-400 text-xs">{r.event_id}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full font-black text-[10px] ${
                            r.risk_level === 'حرج' ? 'bg-purple-100 text-purple-700' :
                            r.risk_level === 'عالي' || r.risk_level === 'مرتفع' ? 'bg-red-100 text-red-700' :
                            r.risk_level === 'متوسط' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {r.risk_level}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">{r.location}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">{r.event_type}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">{r.reporter_name}</td>
                        <td className="px-4 py-3 text-xs text-slate-400">{formatDate(r.created_at)}</td>
                        <td className="px-4 py-3 text-xs text-emerald-600 dark:text-emerald-400 font-bold">
                          {r.closed_at ? formatDate(r.closed_at) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                {archLoading ? (
                  <span className="spinner border-amber-200 border-t-amber-500" />
                ) : (
                  <>
                    <p className="text-4xl mb-2">🗄️</p>
                    <p className="font-bold text-sm">اضغط "بحث في الأرشيف" لتحميل السجلات</p>
                  </>
                )}
              </div>
            )}
          </div>
        )}

      </main>

      {/* ── Modals ── */}
      {showProfile && (
        <AdminProfileModal profile={profile} onClose={() => setShowProfile(false)} />
      )}
      {showLogout && (
        <AdminLogoutModal onConfirm={handleSignOut} onCancel={() => setShowLogout(false)} />
      )}
    </div>
  );
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */

function KpiCard({ label, value, color, icon }: {
  label: string;
  value: number | string;
  color: 'blue' | 'orange' | 'red' | 'emerald';
  icon: string;
}) {
  const borderColor = {
    blue:    'border-blue-500',
    orange:  'border-orange-500',
    red:     'border-red-500',
    emerald: 'border-emerald-500',
  }[color];
  const textColor = {
    blue:    'text-slate-800 dark:text-white',
    orange:  'text-orange-600',
    red:     'text-red-600',
    emerald: 'text-emerald-600',
  }[color];

  return (
    <div className={`glass-panel p-5 rounded-2xl border-r-4 ${borderColor} relative overflow-hidden group hover:shadow-md transition-all`}>
      <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-2">{label}</p>
      <p className={`text-3xl font-black ${textColor}`}>{value}</p>
      <span className="absolute left-3 bottom-3 text-4xl opacity-5 group-hover:opacity-10 transition-opacity select-none">
        {icon}
      </span>
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="space-y-2.5 py-2">
      {[80, 60, 45, 30].map(w => (
        <div key={w} className="flex items-center gap-2">
          <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse" style={{ width: `${w}%` }} />
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   AdminProfileModal
═══════════════════════════════════════════════════════════════════════════ */
function AdminProfileModal({ profile, onClose }: { profile: any; onClose: () => void }) {
  const [phone,      setPhone]      = useState(profile?.phone ?? '');
  const [newPass,    setNewPass]    = useState('');
  const [notifEmail, setNotifEmail] = useState<boolean>(profile?.notif_email ?? true);
  const [saving,     setSaving]     = useState(false);
  const [msg,        setMsg]        = useState('');

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const { error: profErr } = await supabase
        .from('profiles')
        .update({ phone, notif_email: notifEmail })
        .eq('id', profile?.id);
      if (profErr) throw profErr;
      if (newPass) {
        const { error: passErr } = await supabase.auth.updateUser({ password: newPass });
        if (passErr) throw passErr;
      }
      setMsg('تم الحفظ بنجاح ✓');
      setNewPass('');
    } catch (e: any) {
      setMsg('حدث خطأ: ' + (e.message ?? ''));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" dir="rtl" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden border border-slate-200 dark:border-slate-700"
           onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 border-r-4 border-red-500 flex justify-between items-center">
          <h3 className="font-black text-base text-slate-800 dark:text-white">الملف الشخصي</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:bg-red-100 hover:text-red-500 transition text-sm font-black">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">الاسم</label>
            <input className="input-field py-2 text-sm bg-slate-50 dark:bg-slate-800" value={profile?.name ?? ''} readOnly />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">البريد الإلكتروني</label>
            <input className="input-field py-2 text-sm bg-slate-50 dark:bg-slate-800" dir="ltr" value={profile?.email ?? ''} readOnly />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">رقم الجوال</label>
            <input className="input-field py-2 text-sm" dir="ltr" value={phone} onChange={e => setPhone(e.target.value)} placeholder="05xxxxxxxx" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">كلمة مرور جديدة (اختياري)</label>
            <input type="password" className="input-field py-2 text-sm" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="اتركه فارغاً إن لم ترد التغيير" />
          </div>
          <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 rounded-xl px-4 py-3 border border-slate-100 dark:border-slate-700">
            <span className="text-xs font-bold text-slate-700 dark:text-slate-300">تلقّي الإشعارات بالبريد</span>
            <button
              onClick={() => setNotifEmail((v: boolean) => !v)}
              className={`w-11 h-6 rounded-full transition-all relative ${notifEmail ? 'bg-red-600' : 'bg-slate-300 dark:bg-slate-600'}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${notifEmail ? 'right-0.5' : 'left-0.5'}`} />
            </button>
          </div>
          {msg && <p className={`text-xs font-bold text-center ${msg.startsWith('حدث') ? 'text-red-500' : 'text-teal-600'}`}>{msg}</p>}
          <button onClick={save} disabled={saving} className="btn-primary w-full py-2.5 text-sm">
            {saving ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   AdminLogoutModal
═══════════════════════════════════════════════════════════════════════════ */
function AdminLogoutModal({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" dir="rtl">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 border border-slate-200 dark:border-slate-700 text-center">
        <div className="text-4xl mb-3">👋</div>
        <h3 className="font-black text-base text-slate-800 dark:text-white mb-2">تأكيد تسجيل الخروج</h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 font-bold mb-6">هل أنت متأكد من رغبتك في الخروج من لوحة الإدارة؟</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 btn-secondary text-sm py-2.5">إلغاء</button>
          <button onClick={onConfirm} className="flex-1 bg-red-600 hover:bg-red-700 text-white font-black text-sm py-2.5 rounded-xl transition-colors">خروج</button>
        </div>
      </div>
    </div>
  );
}
